import { dehydrate, hydrate, type DehydratedState, type Query, type QueryClient } from "@tanstack/react-query";

// Release 1.5.0 (perf, behind the 1.5.0 switch) — the last answers of the operational screens are kept in THIS TAB'S
// sessionStorage (gone when the tab closes; never on disk after the session, never IndexedDB), so going back to a screen
// or reloading the tab shows the last figures at once while fresh ones load ("stale-while-revalidate").
// Rules (tests in queryPersistence.test.ts):
//   • per person, per set of roles, per release — anything else on reload is thrown away;
//   • never for the wall-display kiosk or the customer presentation;
//   • only the operational read models on the allowlist — never sign-in, admin, audit, custody, VIPER/AI text,
//     notifications, people, training, quality, search or freshness;
//   • cleared on sign-out, on a role change and when the API says nobody is signed in.

export const PERSIST_STORAGE_KEY = "summitops.queryCache.v1";
export const PERSIST_TTL_MS = 12 * 60 * 60 * 1000;
/** One answer larger than this (JSON characters) is not kept. */
export const MAX_QUERY_CHARS = 4_000_000;
/** The whole stored value (after compression and base64) must stay under this, well inside the 5 MB storage quota. */
export const MAX_STORED_CHARS = 3_500_000;

/** Query-key roots that may be kept: the operational read models. */
export const PERSISTABLE_ROOTS: ReadonlySet<string> = new Set([
  "operations",
  "shopfloor",
  "supply",
  "schedule",
  "planning",
  "floor",
  "flow",
  "charts",
  "kanban",
  "purchasing-workspace"
]);

/** Never kept, wherever they appear in a key (release 1.5.0 guardrail). */
const NEVER_PERSIST_PARTS = ["search", "freshness", "admin", "me", "viper", "ai", "insights", "audit", "custody", "notifications", "people"];

export interface PersistIdentity {
  owner: string;
  roles: string;
  release: string;
}

export interface PrincipalLike {
  userPrincipalName?: string | null;
  displayName?: string | null;
  roles: readonly string[];
  release?: string | null;
}

/**
 * Who the kept answers belong to, or null when nothing may be kept (nobody signed in, the kiosk, the customer
 * presentation or the wall board routes).
 */
export function persistIdentity(principal: PrincipalLike | null | undefined, persona: string | null, pathname: string): PersistIdentity | null {
  if (!principal || principal.roles.length === 0) return null;
  if (principal.roles.includes("WallDisplay")) return null;
  if (pathname.startsWith("/display") || pathname.startsWith("/present")) return null;
  const owner = principal.userPrincipalName?.trim() || (persona ? `persona:${persona}` : "");
  if (!owner) return null;
  return { owner: owner.toLowerCase(), roles: [...principal.roles].sort().join(","), release: principal.release ?? "" };
}

export function sameIdentity(a: PersistIdentity | null, b: PersistIdentity | null): boolean {
  return !!a && !!b && a.owner === b.owner && a.roles === b.roles && a.release === b.release;
}

/** True for a query key that may be kept: an allowlisted root and nothing on the never-list anywhere in the key. */
export function isPersistableKey(queryKey: readonly unknown[]): boolean {
  const root = queryKey[0];
  if (typeof root !== "string" || !PERSISTABLE_ROOTS.has(root)) return false;
  return !queryKey.some((part) => typeof part === "string" && NEVER_PERSIST_PARTS.some((n) => part === n || part.startsWith(`${n}-`) || part.endsWith(`-${n}`)));
}

function shouldDehydrateQuery(query: Query): boolean {
  return query.state.status === "success" && query.state.data !== undefined && isPersistableKey(query.queryKey);
}

/** Text codec for the stored value: gzip + base64 where the browser has CompressionStream; tests pass a plain one. */
export interface PersistCodec {
  encode(text: string): Promise<string>;
  decode(stored: string): Promise<string>;
}

export const plainCodec: PersistCodec = {
  encode: async (t) => t,
  decode: async (s) => s
};

/** The browser codec, or null where compression is not available (then nothing is kept). */
export function browserCodec(): PersistCodec | null {
  if (typeof CompressionStream === "undefined" || typeof DecompressionStream === "undefined" || typeof Blob === "undefined") return null;
  return {
    encode: async (text) => {
      const stream = new Blob([text]).stream().pipeThrough(new CompressionStream("gzip"));
      const bytes = new Uint8Array(await new Response(stream).arrayBuffer());
      let binary = "";
      for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
      return btoa(binary);
    },
    decode: async (stored) => {
      const binary = atob(stored);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream("gzip"));
      return await new Response(stream).text();
    }
  };
}

interface StoredEnvelope {
  v: 1;
  identity: PersistIdentity;
  savedAt: number;
  body: string;
}

export function clearPersistedQueries(storage: Storage): void {
  try {
    storage.removeItem(PERSIST_STORAGE_KEY);
  } catch {
    /* storage unavailable: nothing to clear */
  }
}

/** Keep the allowlisted answers for this identity. Returns how many were kept (0 = nothing written). */
export async function saveQueries(queryClient: QueryClient, identity: PersistIdentity, storage: Storage, codec: PersistCodec, now: number = Date.now()): Promise<number> {
  const state = dehydrate(queryClient, { shouldDehydrateQuery, shouldDehydrateMutation: () => false });
  // Largest answers first out when over the budget; any single answer over the cap is never kept.
  let queries = state.queries
    .map((q) => ({ q, size: JSON.stringify(q).length }))
    .filter((x) => x.size <= MAX_QUERY_CHARS)
    .sort((a, b) => a.size - b.size);
  while (queries.length > 0) {
    const kept: DehydratedState = { mutations: [], queries: queries.map((x) => x.q) };
    const body = await codec.encode(JSON.stringify(kept));
    const envelope: StoredEnvelope = { v: 1, identity, savedAt: now, body };
    const text = JSON.stringify(envelope);
    if (text.length <= MAX_STORED_CHARS) {
      try {
        storage.setItem(PERSIST_STORAGE_KEY, text);
        return queries.length;
      } catch {
        /* quota: drop the largest and try again */
      }
    }
    queries = queries.slice(0, -1);
  }
  clearPersistedQueries(storage);
  return 0;
}

/**
 * Put the kept answers back for this identity. Anything kept for someone else, other roles, another release, or more
 * than 12 hours ago is thrown away. Returns how many answers were restored.
 */
export async function restoreQueries(queryClient: QueryClient, identity: PersistIdentity, storage: Storage, codec: PersistCodec, now: number = Date.now()): Promise<number> {
  let raw: string | null = null;
  try {
    raw = storage.getItem(PERSIST_STORAGE_KEY);
  } catch {
    return 0;
  }
  if (!raw) return 0;
  try {
    const envelope = JSON.parse(raw) as StoredEnvelope;
    if (envelope.v !== 1 || !sameIdentity(envelope.identity, identity) || now - envelope.savedAt > PERSIST_TTL_MS || now < envelope.savedAt - 60_000) {
      clearPersistedQueries(storage);
      return 0;
    }
    const state = JSON.parse(await codec.decode(envelope.body)) as DehydratedState;
    const allowed: DehydratedState = { mutations: [], queries: state.queries.filter((q) => isPersistableKey(q.queryKey)) };
    hydrate(queryClient, allowed);
    return allowed.queries.length;
  } catch {
    clearPersistedQueries(storage);
    return 0;
  }
}
